import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function TestimonialsList() {
  const [testimonials, setTestimonials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deletingId, setDeletingId] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  // FETCH TESTIMONIALS
  useEffect(() => {
    let isMounted = true;

    const controller = new AbortController();

    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        setError("");

        const result = await axios.get(
          `${process.env.REACT_APP_API_URL}testimonials/fetchalltestimonials`,
          {
            params: {
              page,
              limit: 10,
              search: searchTerm,
            },
            signal: controller.signal,
          }
        );

        if (!isMounted) return;

        const updatedTestimonials = (result.data.data || []).map(
          (testimonial) => ({
            ...testimonial,
            image: testimonial.image
              ? `${process.env.REACT_APP_API_URL}${testimonial.image.replace(
                /\\/g,
                "/"
              )}`
              : "/default-image.png",
          })
        );

        setTestimonials(updatedTestimonials);

        setTotalPages(
          Math.max(result.data.totalPages || 1, 1)
        );
      } catch (error) {
        if (
          axios.isCancel(error) ||
          error.name === "CanceledError"
        ) {
          return;
        }

        console.log(error);

        if (isMounted) {
          setError(
            error.response?.data?.message ||
            "Failed to fetch testimonials"
          );

          setTestimonials([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTestimonials();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [page, searchTerm]);
  // DELETE CONFIRMATION
  const confirmDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  // DELETE HANDLER
  const handleDelete = async () => {
    setShowDeleteModal(false);
    setDeletingId(deleteTargetId);

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}testimonials/deletetestimonial/${deleteTargetId}`
      );

      toast.success("Testimonial deleted successfully.", {
        position: "top-right",
        autoClose: 1500,
        theme: "colored",
      });

      const updatedList = testimonials.filter(
        (item) => item.testimonialid !== deleteTargetId
      );

      setTestimonials(updatedList);

      if (updatedList.length === 0 && page > 1) {
        setPage((prev) => prev - 1);
      }
    } catch (error) {
      console.log(error);

      toast.error(
        error.response?.data?.message ||
        "Failed to delete testimonial.",
        {
          position: "top-right",
          autoClose: 2000,
          theme: "colored",
        }
      );
    } finally {
      setDeletingId(null);
    }
  };

  // VIEW MODAL
  const handleView = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setShowViewModal(true);
  };

  return (
    <div>
      <ToastContainer />

      <div className="main-card">

        {/* TOOLBAR */}
        <div className="toolbar-container">
          <h2 className="page-title">Testimonials Management</h2>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <span className="results-count">
              {testimonials.length} results
            </span>

            <input
              type="text"
              className="search-box"
              placeholder="Search by Name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />

            <Link
              to="/createTestimonials"
              className="btn-add-primary"
            >
              + Add Testimonial
            </Link>
          </div>
        </div>

        {/* HEADER */}
        <div className="table-header-panel">
          Testimonial List
        </div>

        {/* TABLE */}
        <div style={{ overflowX: "auto" }}>
          <table className="custom-table">
            <thead>
              <tr>
                <th>SR No.</th>
                <th>Image</th>
                <th>ID</th>
                <th>Name</th>
                <th>Name Bio</th>
                <th>Category</th>
                <th>Testimonial</th>
                <th>Status</th>
                <th>Sequence</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {/* LOADING */}
              {loading ? (
                <tr>
                  <td colSpan="10">
                    <div className="loading-container">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                /* ERROR */
                <tr>
                  <td colSpan="10">
                    <div className="empty-state">
                      <div className="empty-state-icon">⚠️</div>

                      <div className="empty-state-text">
                        {error}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : testimonials.length === 0 ? (
                /* EMPTY */
                <tr>
                  <td colSpan="10">
                    <div className="empty-state">
                      <div className="empty-state-icon">📋</div>

                      <div className="empty-state-text">
                        No testimonials found
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                testimonials.map((testimonial, index) => (
                  <tr key={testimonial.testimonialid}>
                    <td>{(page - 1) * 10 + index + 1}</td>

                    {/* IMAGE */}
                    <td>
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        loading="lazy"
                        className="table-img"
                        onError={(e) => {
                          e.target.src = "/default-image.png";
                        }}
                      />
                    </td>

                    {/* ID */}
                    <td>{testimonial.testimonialid}</td>

                    {/* NAME */}
                    <td>{testimonial.name}</td>

                    {/* BIO */}
                    <td>{testimonial.name_bio}</td>

                    {/* CATEGORY */}
                    <td>{testimonial.category}</td>

                    {/* TESTIMONIAL */}
                    <td
                      style={{
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {decodeHtml(
                        testimonial.testimonial || ""
                      ).replace(/<[^>]*>?/gm, "")}
                    </td>

                    {/* STATUS */}
                    <td>
                      <span
                        className={
                          testimonial.isactive
                            ? "badge-active"
                            : "badge-inactive"
                        }
                      >
                        {testimonial.isactive
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </td>

                    {/* SEQUENCE */}
                    <td>{testimonial.sequence}</td>

                    {/* ACTIONS */}
                    <td>
                      <div className="action-btn-container">
                        {/* VIEW */}
                        <button
                          className="btn-action-edit"
                          style={{
                            borderColor: "#17a2b8",
                            color: "#17a2b8",
                            background: "white",
                          }}
                          onClick={() =>
                            handleView(testimonial)
                          }
                        >
                          View
                        </button>

                        {/* EDIT */}
                        <Link
                          to={`/testimonialUpdate/${testimonial.testimonialid}`}
                          className="btn-action-edit"
                        >
                          Edit
                        </Link>

                        {/* DELETE */}
                        <button
                          onClick={() =>
                            confirmDelete(
                              testimonial.testimonialid
                            )
                          }
                          className="btn-action-delete"
                          disabled={
                            deletingId ===
                            testimonial.testimonialid
                          }
                        >
                          {deletingId ===
                            testimonial.testimonialid
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div
          style={{
            marginTop: "20px",
            textAlign: "center",
          }}
        >
          <button
            disabled={page === 1}
            onClick={() =>
              setPage((prev) => Math.max(prev - 1, 1))
            }
            style={{ marginRight: "10px" }}
          >
            Prev
          </button>

          {/* PAGE SELECTOR */}
          <select
            value={page}
            onChange={(e) =>
              setPage(Number(e.target.value))
            }
            style={{
              margin: "0 10px",
              padding: "5px",
            }}
          >
            {Array.from(
              { length: totalPages },
              (_, i) => (
                <option key={i + 1} value={i + 1}>
                  Page {i + 1}
                </option>
              )
            )}
          </select>

          <span> of {totalPages} </span>

          <button
            disabled={page === totalPages}
            onClick={() =>
              setPage((prev) =>
                Math.min(prev + 1, totalPages)
              )
            }
            style={{ marginLeft: "10px" }}
          >
            Next
          </button>
        </div>
      </div>

      {/* DELETE MODAL */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Are you sure you want to delete this
          testimonial?
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setShowDeleteModal(false)
            }
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* VIEW MODAL */}
      {showViewModal && selectedTestimonial && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            backgroundColor:
              "rgba(0,0,0,0.6)",
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">

              {/* HEADER */}
              <div
                className="modal-header"
                style={{
                  backgroundColor: "#17a2b8",
                  color: "white",
                }}
              >
                <h5 className="modal-title">
                  Full Testimonial Profile:{" "}
                  {selectedTestimonial.name}
                </h5>

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() =>
                    setShowViewModal(false)
                  }
                ></button>
              </div>

              {/* BODY */}
              <div
                className="modal-body"
                style={{
                  maxHeight: "75vh",
                  overflowY: "auto",
                }}
              >
                <div className="row g-3">

                  {/* IMAGE */}
                  <div className="col-12 text-center mb-3">
                    <img
                      src={selectedTestimonial.image}
                      alt="Profile"
                      style={{
                        height: "120px",
                        width: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border:
                          "2px solid #17a2b8",
                      }}
                    />
                  </div>

                  {[
                    {
                      label: "Serial No",
                      value:
                        selectedTestimonial.srno,
                    },
                    {
                      label: "Testimonial ID",
                      value:
                        selectedTestimonial.testimonialid,
                    },
                    {
                      label: "Full Name",
                      value:
                        selectedTestimonial.name,
                    },
                    {
                      label: "Bio / Designation",
                      value:
                        selectedTestimonial.name_bio,
                    },
                    {
                      label: "Category",
                      value:
                        selectedTestimonial.category,
                    },
                    {
                      label: "Display Sequence",
                      value:
                        selectedTestimonial.sequence,
                    },
                    {
                      label: "Account Status",
                      value:
                        selectedTestimonial.isactive
                          ? "Active"
                          : "Inactive",
                    },
                  ].map((item) => (
                    <div
                      className="col-md-6"
                      key={item.label}
                    >
                      <div
                        className="p-2"
                        style={{
                          backgroundColor:
                            "#f8f9fa",
                          borderRadius: "5px",
                          borderLeft:
                            "4px solid #17a2b8",
                        }}
                      >
                        <strong
                          className="text-secondary d-block"
                          style={{
                            fontSize: "0.75rem",
                            textTransform:
                              "uppercase",
                          }}
                        >
                          {item.label}
                        </strong>

                        <span className="text-dark fw-bold">
                          {item.value ?? "N/A"}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* CONTENT */}
                  <div className="col-12 mt-3">
                    <div
                      className="p-3"
                      style={{
                        backgroundColor:
                          "#fff3cd",
                        borderRadius: "5px",
                        border:
                          "1px dashed #ffc107",
                      }}
                    >
                      <strong
                        className="text-warning d-block mb-2"
                        style={{
                          fontSize: "0.8rem",
                          textTransform:
                            "uppercase",
                        }}
                      >
                        Testimonial Content
                      </strong>

                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            decodeHtml(
                              selectedTestimonial.testimonial
                            ) ||
                            "<em>No content provided.</em>",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-info text-white px-4"
                  onClick={() =>
                    setShowViewModal(false)
                  }
                >
                  Done
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}